import ResearchSearchBar from "./ResearchSearchBar";
import ResearchTimeline from "./ResearchTimeline";
import { generateDefaultQuestions } from "./researchDefaults";
import {
  CloseRounded as CloseIcon,
  LanguageRounded as WebIcon,
  SmartToyRounded as AiIcon,
} from "@mui/icons-material";
import {
  Badge,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import * as logger from "common/logger";
import { useSnackbar } from "notistack";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import * as researchService from "services/researchService";
import * as attachmentsActions from "store/actions/input/attachments/attachmentsActions";
import * as programSelectors from "store/selectors/input/program/programSelectors";

const DRAWER_WIDTH = 480;

const SuggestedQuestion = ({ question, onClick, disabled }) => {
  const Icon = question.itemType === "web_search" ? WebIcon : AiIcon;
  return (
    <Tooltip title={question.query} placement={"top"}>
      <Chip
        icon={<Icon sx={{ fontSize: 16 }} />}
        label={question.label}
        size={"small"}
        clickable
        onClick={onClick}
        disabled={disabled}
        variant={"outlined"}
        sx={{
          "& .MuiChip-icon": {
            color:
              question.itemType === "web_search"
                ? "primary.main"
                : "secondary.main",
          },
        }}
      />
    </Tooltip>
  );
};

const ResearchPanel = ({ isOpen, onClose }) => {
  const { submissionId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const program = useSelector(programSelectors.selectProgram);
  const rawClaims = useSelector((state) => state.input?.claims ?? null);
  const rawExposure = useSelector((state) => state.input?.exposure ?? null);
  const submissionContext = useMemo(
    () => ({
      ...program,
      claims: rawClaims,
      exposure: rawExposure,
    }),
    [program, rawClaims, rawExposure]
  );
  const [isSearching, setIsSearching] = useState(false);
  const [updatingItemId, setUpdatingItemId] = useState(null);

  const {
    data: session,
    isLoading,
    refetch,
  } = researchService.useGetSessionQuery(
    { submissionId },
    { skip: !isOpen || !submissionId }
  );

  const [addItem] = researchService.useAddItemMutation();
  const [updateItem] = researchService.useUpdateItemMutation();
  const [citeSource] = researchService.useCiteSourceMutation();
  const [deleteItem] = researchService.useDeleteItemMutation();
  const [addReferenceFiling] = researchService.useAddReferenceFilingMutation();
  const [
    saveFilingAttachment,
  ] = researchService.useSaveFilingAttachmentMutation();
  const [aiFollowup] = researchService.useAiFollowupMutation();

  const handleSearch = useCallback(
    async (query, mode, options = {}) => {
      setIsSearching(true);
      try {
        const programData = options.includeSubmissionContext
          ? submissionContext
          : undefined;
        const response = await addItem({
          submissionId,
          itemType: mode,
          query,
          programData,
        });
        if (response.error) {
          throw new Error("Failed to add research item");
        }
        await refetch();
      } catch (e) {
        logger.exception(e);
        enqueueSnackbar("Error performing search. Please try again.", {
          variant: "error",
          autoHideDuration: 5000,
        });
      } finally {
        setIsSearching(false);
      }
    },
    [submissionId, addItem, refetch, enqueueSnackbar, submissionContext]
  );

  const handleAddReferenceFiling = useCallback(
    async (identifier) => {
      setIsSearching(true);
      try {
        const response = await addReferenceFiling({
          submissionId,
          identifier,
        });
        if (response.error) {
          const detail =
            response.error?.data?.message ||
            response.error?.message ||
            "Could not fetch filings for that identifier.";
          enqueueSnackbar(detail, {
            variant: "error",
            autoHideDuration: 6000,
          });
          return;
        }
        await refetch();
      } catch (e) {
        logger.exception(e);
        enqueueSnackbar("Error fetching filings. Please try again.", {
          variant: "error",
          autoHideDuration: 5000,
        });
      } finally {
        setIsSearching(false);
      }
    },
    [submissionId, addReferenceFiling, refetch, enqueueSnackbar]
  );

  const handleAddNote = useCallback(async () => {
    try {
      const response = await addItem({
        submissionId,
        itemType: "manual_note",
        resultText: "",
        note: "",
        provider: "manual",
      });
      if (response.error) {
        throw new Error("Failed to add note");
      }
      await refetch();
    } catch (e) {
      logger.exception(e);
      enqueueSnackbar("Error adding note. Please try again.", {
        variant: "error",
        autoHideDuration: 5000,
      });
    }
  }, [submissionId, addItem, refetch, enqueueSnackbar]);

  const handleUpdateItem = useCallback(
    async (itemId, updates) => {
      setUpdatingItemId(itemId);
      try {
        const response = await updateItem({
          submissionId,
          itemId,
          ...updates,
        });
        if (response.error) {
          throw new Error("Failed to update item");
        }
        await refetch();
      } catch (e) {
        logger.exception(e);
        enqueueSnackbar("Error updating item. Please try again.", {
          variant: "error",
          autoHideDuration: 5000,
        });
      } finally {
        setUpdatingItemId(null);
      }
    },
    [submissionId, updateItem, refetch, enqueueSnackbar]
  );

  const handleCiteSource = useCallback(
    async (itemId, sourceUrl, cited, citedIn) => {
      setUpdatingItemId(itemId);
      try {
        const response = await citeSource({
          submissionId,
          itemId,
          sourceUrl,
          cited,
          citedIn,
        });
        if (response.error) {
          throw new Error("Failed to cite source");
        }
        await refetch();
      } catch (e) {
        logger.exception(e);
        enqueueSnackbar("Error updating citation. Please try again.", {
          variant: "error",
          autoHideDuration: 5000,
        });
      } finally {
        setUpdatingItemId(null);
      }
    },
    [submissionId, citeSource, refetch, enqueueSnackbar]
  );

  const handleSaveAttachment = useCallback(
    async (itemId) => {
      setUpdatingItemId(itemId);
      try {
        const response = await saveFilingAttachment({
          submissionId,
          itemId,
        });
        if (response.error) {
          const detail =
            response.error?.data?.message ||
            response.error?.message ||
            "Could not save filing as attachment.";
          enqueueSnackbar(detail, {
            variant: "error",
            autoHideDuration: 6000,
          });
          return;
        }
        const attachmentId = response.data?.attachmentId;
        if (attachmentId) {
          dispatch(attachmentsActions.fileUploaded(attachmentId));
        }
        await refetch();
        enqueueSnackbar(
          `Saved as attachment: ${response.data?.filename || "filing"}`,
          {
            variant: "success",
            autoHideDuration: 4000,
          }
        );
      } catch (e) {
        logger.exception(e);
        enqueueSnackbar("Error saving filing. Please try again.", {
          variant: "error",
          autoHideDuration: 5000,
        });
      } finally {
        setUpdatingItemId(null);
      }
    },
    [submissionId, saveFilingAttachment, refetch, enqueueSnackbar, dispatch]
  );

  const handleFollowUp = useCallback(
    async (itemId, query, options = {}) => {
      setUpdatingItemId(itemId);
      try {
        const programData = options.includeSubmissionContext
          ? submissionContext
          : undefined;
        const response = await aiFollowup({
          submissionId,
          itemId,
          query,
          programData,
        });
        if (response.error) {
          const detail =
            response.error?.data?.message ||
            response.error?.message ||
            "Could not run follow-up question.";
          enqueueSnackbar(detail, {
            variant: "error",
            autoHideDuration: 5000,
          });
          return;
        }
        await refetch();
      } catch (e) {
        logger.exception(e);
        enqueueSnackbar("Error on follow-up. Please try again.", {
          variant: "error",
          autoHideDuration: 5000,
        });
      } finally {
        setUpdatingItemId(null);
      }
    },
    [submissionId, aiFollowup, refetch, enqueueSnackbar, submissionContext]
  );

  const handleDeleteItem = useCallback(
    async (itemId) => {
      setUpdatingItemId(itemId);
      try {
        const response = await deleteItem({ submissionId, itemId });
        if (response.error) {
          throw new Error("Failed to delete item");
        }
        await refetch();
      } catch (e) {
        logger.exception(e);
        enqueueSnackbar("Error deleting item. Please try again.", {
          variant: "error",
          autoHideDuration: 5000,
        });
      } finally {
        setUpdatingItemId(null);
      }
    },
    [submissionId, deleteItem, refetch, enqueueSnackbar]
  );

  const citedCount = (session?.items || []).reduce((count, item) => {
    if (item.isCited) return count + 1;
    const citedSources = (item.sources || []).filter((s) => s.cited);
    return count + citedSources.length;
  }, 0);

  const suggestedQuestions = useMemo(() => {
    const defaults = generateDefaultQuestions(program);
    const runQueries = new Set(
      (session?.items || [])
        .map((item) => item.query?.toLowerCase())
        .filter(Boolean)
    );
    return defaults.filter((q) => !runQueries.has(q.query.toLowerCase()));
  }, [program, session?.items]);

  return (
    <Drawer
      anchor={"right"}
      open={isOpen}
      onClose={onClose}
      variant={"persistent"}
      sx={{
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          boxSizing: "border-box",
          top: 64,
          height: "calc(100% - 64px)",
        },
      }}
    >
      <Stack sx={{ height: "100%", overflow: "hidden" }}>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          sx={{ p: 1.5, pb: 1 }}
        >
          <Stack direction={"row"} alignItems={"center"} spacing={1}>
            <Typography variant={"h6"} fontSize={"1rem"}>
              {"Research"}
            </Typography>
            {citedCount > 0 && (
              <Badge badgeContent={citedCount} color={"primary"} max={99}>
                <Typography variant={"caption"} color={"text.secondary"}>
                  {"cited"}
                </Typography>
              </Badge>
            )}
          </Stack>
          <IconButton size={"small"} onClick={onClose}>
            <CloseIcon fontSize={"small"} />
          </IconButton>
        </Stack>

        <Divider />

        <Stack sx={{ p: 1.5, pb: 1 }}>
          <ResearchSearchBar
            onSearch={handleSearch}
            onAddNote={handleAddNote}
            onAddReferenceFiling={handleAddReferenceFiling}
            isSearching={isSearching}
          />
        </Stack>

        {suggestedQuestions.length > 0 && (
          <>
            <Divider />
            <Stack sx={{ p: 1.5, pb: 1 }} spacing={0.75}>
              <Typography
                variant={"caption"}
                color={"text.secondary"}
                fontWeight={600}
              >
                {"Suggested"}
              </Typography>
              <Stack
                direction={"row"}
                spacing={0.5}
                flexWrap={"wrap"}
                useFlexGap
              >
                {suggestedQuestions.map((question) => (
                  <SuggestedQuestion
                    key={question.label}
                    question={question}
                    disabled={isSearching}
                    onClick={() =>
                      handleSearch(question.query, question.itemType)
                    }
                  />
                ))}
              </Stack>
            </Stack>
          </>
        )}

        <Divider />

        <Stack sx={{ flex: 1, overflow: "auto", p: 1.5 }}>
          <ResearchTimeline
            items={session?.items}
            isLoading={isLoading}
            onUpdateItem={handleUpdateItem}
            onCiteSource={handleCiteSource}
            onDeleteItem={handleDeleteItem}
            onSaveAttachment={handleSaveAttachment}
            onFollowUp={handleFollowUp}
            updatingItemId={updatingItemId}
          />
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default ResearchPanel;
